import axiosInstance from '@/core/api'

import type { components, operations } from '@/schema'

// Response types from schema
type AiDraftResponse = components['schemas']['AiDraftResponse']
type ChatRoomListResponse = components['schemas']['ChatRoomListResponse']
type ChatRoomResponse = components['schemas']['ChatRoomResponse']
type ChatMessageListResponse = components['schemas']['ChatMessageListResponse']
type SendChatMessageResponse =
	operations['ChatController_sendMessage']['responses']['201']['content']['application/json']
type CustomerTagResponse = components['schemas']['CustomerTagResponse']
type QuickResponseResponse = components['schemas']['QuickResponseResponse']
type ChatSettingsResponse = components['schemas']['ChatSettingsResponse']
type LineQuotaResponse =
	operations['ChatController_getQuota']['responses']['200']['content']['application/json']
type ChatAnalyticsResponse = components['schemas']['ChatAnalyticsResponse']
type SuccessResponse = components['schemas']['SuccessResponse']
type MarkAsReadResponse =
	operations['ChatController_markAsRead']['responses']['200']['content']['application/json']
type ChatMediaUploadUrlResponse =
	components['schemas']['ChatMediaUploadUrlResponse']
type GetLineStickerPackagesResponse =
	components['schemas']['GetLineStickerPackagesResponse']
type UnreadCountResponse = components['schemas']['UnreadCountResponse']

// Request types from schema
type SendChatMessageDto = components['schemas']['SendChatMessageDto']
type SendMultipleMessagesDto = components['schemas']['SendMultipleMessagesDto']
type SendMultipleMessagesResponse =
	components['schemas']['SendMultipleMessagesResponse']
type CreateCustomerTagDto = components['schemas']['CreateCustomerTagDto']
type UpdateCustomerTagDto = components['schemas']['UpdateCustomerTagDto']
type AddTagsToRoomDto = components['schemas']['AddTagsToRoomDto']
type CreateQuickResponseDto = components['schemas']['CreateQuickResponseDto']
type UpdateQuickResponseDto = components['schemas']['UpdateQuickResponseDto']
type UpdateChatSettingsDto = components['schemas']['UpdateChatSettingsDto']
type GenerateChatMediaUploadUrlDto =
	components['schemas']['GenerateChatMediaUploadUrlDto']
export type LinkPlayerToRoomDto = components['schemas']['LinkPlayerToRoomDto']

type UpdateChatRoomStatusDtoStatus =
	components['schemas']['UpdateChatRoomStatusDto']['status']

type ChatMessageResponse = components['schemas']['ChatMessageResponse']
type ChatNoteResponse = components['schemas']['ChatNoteResponse']
type ChatNoteListResponse = components['schemas']['ChatNoteListResponse']
type CreateChatNoteDto = components['schemas']['CreateChatNoteDto']
type UpdateChatNoteDto = components['schemas']['UpdateChatNoteDto']

// Query parameter types
type GetRoomsParams =
	operations['ChatController_getChatRooms']['parameters']['query']
type GetMessagesParams =
	operations['ChatController_getChatMessages']['parameters']['query']
type GetAnalyticsParams =
	operations['ChatController_getAnalytics']['parameters']['query']
type GetBannedRoomsParams =
	operations['ChatController_getBannedRooms']['parameters']['query']
type GetClosedRoomsParams =
	operations['ChatController_getClosedRooms']['parameters']['query']

class ChatService {
	// ==================== ROOMS ====================

	async getRooms(params?: GetRoomsParams): Promise<ChatRoomListResponse> {
		const response = await axiosInstance.get<ChatRoomListResponse>(
			'/admin/chat/rooms',
			{ params },
		)
		return response.data
	}

	async getUnreadCount(): Promise<UnreadCountResponse> {
		const response = await axiosInstance.get<UnreadCountResponse>(
			'/admin/chat/unread-count',
		)
		return response.data
	}

	async getRoom(id: string): Promise<ChatRoomResponse> {
		const response = await axiosInstance.get<ChatRoomResponse>(
			`/admin/chat/rooms/${id}`,
		)
		return response.data
	}

	async claimRoom(id: string): Promise<ChatRoomResponse> {
		const response = await axiosInstance.post<ChatRoomResponse>(
			`/admin/chat/rooms/${id}/claim`,
		)
		return response.data
	}

	async releaseRoom(id: string): Promise<ChatRoomResponse> {
		const response = await axiosInstance.delete<ChatRoomResponse>(
			`/admin/chat/rooms/${id}/claim`,
		)
		return response.data
	}

	async linkPlayerToRoom(
		roomId: string,
		data: LinkPlayerToRoomDto,
	): Promise<ChatRoomResponse> {
		const response = await axiosInstance.post<ChatRoomResponse>(
			`/admin/chat/rooms/${roomId}/player`,
			data,
		)
		return response.data
	}

	async unlinkPlayerFromRoom(roomId: string): Promise<ChatRoomResponse> {
		const response = await axiosInstance.delete<ChatRoomResponse>(
			`/admin/chat/rooms/${roomId}/player`,
		)
		return response.data
	}

	async updateRoomStatus(
		roomId: string,
		status: UpdateChatRoomStatusDtoStatus,
	): Promise<ChatRoomResponse> {
		const response = await axiosInstance.post<ChatRoomResponse>(
			`/admin/chat/rooms/${roomId}/status`,
			{ status },
		)
		return response.data
	}

	// ==================== MESSAGES ====================

	async getMessages(
		roomId: string,
		params?: GetMessagesParams,
	): Promise<ChatMessageListResponse> {
		const response = await axiosInstance.get<ChatMessageListResponse>(
			`/admin/chat/rooms/${roomId}/messages`,
			{ params },
		)
		return response.data
	}

	async sendMessage(
		roomId: string,
		data: SendChatMessageDto,
	): Promise<SendChatMessageResponse> {
		const response = await axiosInstance.post<SendChatMessageResponse>(
			`/admin/chat/rooms/${roomId}/messages`,
			data,
		)
		return response.data
	}

	async sendMultipleMessages(
		roomId: string,
		data: SendMultipleMessagesDto,
	): Promise<SendMultipleMessagesResponse> {
		const response = await axiosInstance.post<SendMultipleMessagesResponse>(
			`/admin/chat/rooms/${roomId}/messages/multiple`,
			data,
		)
		return response.data
	}

	async markAsRead(roomId: string): Promise<MarkAsReadResponse> {
		const response = await axiosInstance.post<MarkAsReadResponse>(
			`/admin/chat/rooms/${roomId}/read`,
		)
		return response.data
	}

	async searchMessages(params: {
		query?: string
		roomId?: string
		direction?: 'INCOMING' | 'OUTGOING'
		dateFrom?: string
		dateTo?: string
		limit?: number
	}): Promise<{
		items: Array<{
			message: ChatMessageResponse
			room: {
				id: string
				lineDisplayName?: string
				linePictureUrl?: string
			}
		}>
		pagination: components['schemas']['CursorPaginationMeta']
		totalCount: number
	}> {
		const response = await axiosInstance.get('/admin/chat/search', { params })
		return response.data
	}

	async getPinnedMessages(roomId: string): Promise<ChatMessageResponse[]> {
		const response = await axiosInstance.get<ChatMessageResponse[]>(
			`/admin/chat/rooms/${roomId}/pinned-messages`,
		)
		return response.data
	}

	/**
	 * Get messages around a specific message cursor.
	 * Fetches messages with the target message in the middle (both older and newer).
	 */
	async getMessagesAroundCursor(
		roomId: string,
		messageId: string,
		limit = 50,
	): Promise<ChatMessageListResponse> {
		const response = await axiosInstance.get<ChatMessageListResponse>(
			`/admin/chat/rooms/${roomId}/messages`,
			{
				params: {
					start: messageId,
					limit,
				},
			},
		)
		return response.data
	}

	async pinMessage(
		roomId: string,
		messageId: string,
		isPinned: boolean,
	): Promise<ChatMessageResponse> {
		const response = await axiosInstance.post<ChatMessageResponse>(
			`/admin/chat/rooms/${roomId}/messages/${messageId}/pin`,
			{ isPinned },
		)
		return response.data
	}

	async pinRoom(roomId: string, isPinned: boolean): Promise<ChatRoomResponse> {
		const response = await axiosInstance.post<ChatRoomResponse>(
			`/admin/chat/rooms/${roomId}/pin`,
			{ isPinned },
		)
		return response.data
	}

	async banRoom(
		roomId: string,
		isBanned: boolean,
		reason?: string,
	): Promise<ChatRoomResponse> {
		const response = await axiosInstance.post<ChatRoomResponse>(
			`/admin/chat/rooms/${roomId}/ban`,
			{ isBanned, reason },
		)
		return response.data
	}

	async getBannedRooms(
		params?: GetBannedRoomsParams,
	): Promise<ChatRoomListResponse> {
		const response = await axiosInstance.get<ChatRoomListResponse>(
			'/admin/chat/rooms/banned',
			{ params },
		)
		return response.data
	}

	async getClosedRooms(
		params?: GetClosedRoomsParams,
	): Promise<ChatRoomListResponse> {
		const response = await axiosInstance.get<ChatRoomListResponse>(
			'/admin/chat/rooms/closed',
			{ params },
		)
		return response.data
	}

	// ==================== TAGS ====================

	async getTags(): Promise<CustomerTagResponse[]> {
		const response =
			await axiosInstance.get<CustomerTagResponse[]>('/admin/chat/tags')
		return response.data
	}

	async createTag(data: CreateCustomerTagDto): Promise<CustomerTagResponse> {
		const response = await axiosInstance.post<CustomerTagResponse>(
			'/admin/chat/tags',
			data,
		)
		return response.data
	}

	async updateTag(
		id: string,
		data: UpdateCustomerTagDto,
	): Promise<CustomerTagResponse> {
		const response = await axiosInstance.put<CustomerTagResponse>(
			`/admin/chat/tags/${id}`,
			data,
		)
		return response.data
	}

	async deleteTag(id: string): Promise<SuccessResponse> {
		const response = await axiosInstance.delete<SuccessResponse>(
			`/admin/chat/tags/${id}`,
		)
		return response.data
	}

	async addTagsToRoom(
		roomId: string,
		data: AddTagsToRoomDto,
	): Promise<ChatRoomResponse> {
		const response = await axiosInstance.post<ChatRoomResponse>(
			`/admin/chat/rooms/${roomId}/tags`,
			data,
		)
		return response.data
	}

	async removeTagFromRoom(
		roomId: string,
		tagId: string,
	): Promise<ChatRoomResponse> {
		const response = await axiosInstance.delete<ChatRoomResponse>(
			`/admin/chat/rooms/${roomId}/tags/${tagId}`,
		)
		return response.data
	}

	// ==================== QUICK RESPONSES ====================

	async getQuickResponses(): Promise<QuickResponseResponse[]> {
		const response = await axiosInstance.get<QuickResponseResponse[]>(
			'/admin/chat/quick-responses',
		)
		return response.data
	}

	async createQuickResponse(
		data: CreateQuickResponseDto,
	): Promise<QuickResponseResponse> {
		const response = await axiosInstance.post<QuickResponseResponse>(
			'/admin/chat/quick-responses',
			data,
		)
		return response.data
	}

	async updateQuickResponse(
		id: string,
		data: UpdateQuickResponseDto,
	): Promise<QuickResponseResponse> {
		const response = await axiosInstance.put<QuickResponseResponse>(
			`/admin/chat/quick-responses/${id}`,
			data,
		)
		return response.data
	}

	async deleteQuickResponse(id: string): Promise<SuccessResponse> {
		const response = await axiosInstance.delete<SuccessResponse>(
			`/admin/chat/quick-responses/${id}`,
		)
		return response.data
	}

	// ==================== SETTINGS ====================

	async getSettings(): Promise<ChatSettingsResponse> {
		const response = await axiosInstance.get<ChatSettingsResponse>(
			'/admin/chat/settings',
		)
		return response.data
	}

	async updateSettings(
		data: UpdateChatSettingsDto,
	): Promise<ChatSettingsResponse> {
		const response = await axiosInstance.put<ChatSettingsResponse>(
			'/admin/chat/settings',
			data,
		)
		return response.data
	}

	// ==================== QUOTA & ANALYTICS ====================

	async getQuota(): Promise<LineQuotaResponse> {
		const response =
			await axiosInstance.get<LineQuotaResponse>('/admin/chat/quota')
		return response.data
	}

	async getAnalytics(
		params?: GetAnalyticsParams,
	): Promise<ChatAnalyticsResponse> {
		const response = await axiosInstance.get<ChatAnalyticsResponse>(
			'/admin/chat/analytics',
			{ params },
		)
		return response.data
	}

	// ==================== MEDIA UPLOAD ====================

	async generateMediaUploadUrl(
		data: GenerateChatMediaUploadUrlDto,
	): Promise<ChatMediaUploadUrlResponse> {
		const response = await axiosInstance.post<ChatMediaUploadUrlResponse>(
			'/admin/chat/media/upload-url',
			data,
		)
		return response.data
	}

	async uploadFileToPresignedUrl(
		presignedUrl: string,
		file: File,
	): Promise<void> {
		await fetch(presignedUrl, {
			method: 'PUT',
			body: file,
			headers: {
				'Content-Type': file.type,
			},
		})
	}

	// ==================== STICKERS ====================

	async getStickerPackages(): Promise<GetLineStickerPackagesResponse> {
		const response = await axiosInstance.get<GetLineStickerPackagesResponse>(
			'/admin/line-stickers/packages',
			{
				params: {
					includeStickers: true,
					activeOnly: true,
				},
			},
		)
		return response.data
	}

	// ==================== CHAT NOTES ====================

	async getChatNotes(roomId: string): Promise<ChatNoteListResponse> {
		const response = await axiosInstance.get<ChatNoteListResponse>(
			`/admin/chat/rooms/${roomId}/notes`,
		)
		return response.data
	}

	async createChatNote(
		roomId: string,
		data: CreateChatNoteDto,
	): Promise<ChatNoteResponse> {
		const response = await axiosInstance.post<ChatNoteResponse>(
			`/admin/chat/rooms/${roomId}/notes`,
			data,
		)
		return response.data
	}

	async updateChatNote(
		noteId: string,
		data: UpdateChatNoteDto,
	): Promise<ChatNoteResponse> {
		const response = await axiosInstance.put<ChatNoteResponse>(
			`/admin/chat/notes/${noteId}`,
			data,
		)
		return response.data
	}

	async deleteChatNote(noteId: string): Promise<SuccessResponse> {
		const response = await axiosInstance.delete<SuccessResponse>(
			`/admin/chat/notes/${noteId}`,
		)
		return response.data
	}

	// ==================== CONNECT LINE ====================

	async connectLine(roomId: string): Promise<ChatRoomResponse> {
		const response = await axiosInstance.post<ChatRoomResponse>(
			`/admin/chat/rooms/${roomId}/connect-line`,
		)
		return response.data
	}

	// ==================== BROADCAST ====================

	async broadcastMessages(
		data: BroadcastMessagesDto,
	): Promise<BroadcastResponse> {
		const response = await axiosInstance.post<BroadcastResponse>(
			'/admin/chat/broadcast',
			data,
		)
		return response.data
	}

	// ==================== PLAYER STATS ====================

	async getPlayerChatStats(roomId: string): Promise<PlayerChatStatsResponse> {
		const response = await axiosInstance.get<PlayerChatStatsResponse>(
			`/admin/chat/rooms/${roomId}/player-stats`,
		)
		return response.data
	}

	// ==================== AI DRAFT ====================

	async getLatestAiDraft(roomId: string): Promise<AiDraftResponse | null> {
		const response = await axiosInstance.get<AiDraftResponse | null>(
			`/admin/chat/ai-draft/room/${roomId}/latest`,
		)
		return response.data
	}
	async getAiSetting(): Promise<AiSettingFullResponse> {
		const response = await axiosInstance.get<AiSettingFullResponse>(
			'/admin/chat/ai-draft/setting',
		)
		return response.data
	}

	async updateAiSetting(data: {
		systemContext: string
		toolsEnabled?: Record<string, boolean>
		aiEnabled?: boolean
	}) {
		await axiosInstance.put('/admin/chat/ai-draft/setting', data)
	}

	async updateAiPromptCases(
		cases: AiPromptCaseUpdateItem[],
	): Promise<{ success: boolean; updated: number }> {
		const response = await axiosInstance.patch<{
			success: boolean
			updated: number
		}>('/admin/chat/ai-draft/setting/cases', { cases })
		return response.data
	}

	async updateChatAiMode(
		roomId: string,
		aiAnswerMode: AiAnswerMode,
	): Promise<ChatAiModeResponse> {
		const response = await axiosInstance.patch<ChatAiModeResponse>(
			`/admin/chat/ai-draft/room/${roomId}/ai-mode`,
			{ aiAnswerMode },
		)
		return response.data
	}

	async markAiDraftUsed(draftId: string, finalText?: string) {
		await axiosInstance.patch(`/admin/chat/ai-draft/${draftId}/use`, {
			finalText,
		})
	}

	async markAiDraftEdited(draftId: string, finalText: string) {
		await axiosInstance.patch(`/admin/chat/ai-draft/${draftId}/edit`, {
			finalText,
		})
	}

	async markAiDraftIgnored(draftId: string) {
		await axiosInstance.patch(`/admin/chat/ai-draft/${draftId}/ignore`, {})
	}

	// ==================== ADMIN TASKS ====================

	async getRoomAdminTasks(roomId: string): Promise<AdminTaskResponse[]> {
		const response = await axiosInstance.get<AdminTaskResponse[]>(
			`/admin/chat/ai-draft/room/${roomId}/admin-tasks`,
		)
		return response.data
	}

	async markAdminTaskDone(taskId: string): Promise<AdminTaskResponse> {
		const response = await axiosInstance.patch<AdminTaskResponse>(
			`/admin/chat/ai-draft/admin-task/${taskId}/done`,
		)
		return response.data
	}

	async dismissAdminTask(taskId: string): Promise<AdminTaskResponse> {
		const response = await axiosInstance.patch<AdminTaskResponse>(
			`/admin/chat/ai-draft/admin-task/${taskId}/dismiss`,
		)
		return response.data
	}

	async reopenAdminTask(taskId: string): Promise<AdminTaskResponse> {
		const response = await axiosInstance.patch<AdminTaskResponse>(
			`/admin/chat/ai-draft/admin-task/${taskId}/reopen`,
		)
		return response.data
	}
}

export type BroadcastMessagesDto = components['schemas']['BroadcastMessagesDto']
export type BroadcastResponse = components['schemas']['BroadcastResponse']
export type PlayerChatStatsResponse =
	components['schemas']['PlayerChatStatsResponse']

export type AiAnswerMode = 'ASSIS' | 'AUTO' | 'OFF'

export type AiPromptCaseKey =
	| 'deposit_help'
	| 'withdraw_help'
	| 'balance_check'
	| 'bank_info_request'
	| 'promotion_inquiry'
	| 'commission_inquiry'
	| 'returnloss_inquiry'
	| 'lucky_wheel_inquiry'
	| 'daily_checkin_inquiry'
	| 'game_issue'
	| 'login_password'
	| 'account_change'
	| 'complaint'
	| 'chitchat_greeting'
	| 'sticker_reaction'
	| 'sticker_end_conversation'
	| 'spam_irrelevant'
	| 'other_unclear'

export type AiPromptCase = {
	caseKey: AiPromptCaseKey
	replyPrompt: string
	adminPrompt: string
	enabled: boolean
}

export type AiSettingFullResponse = {
	systemContext: string
	toolsEnabled: Record<string, boolean>
	aiEnabled: boolean
	promptCases: AiPromptCase[]
}

export type AiPromptCaseUpdateItem = {
	caseKey: AiPromptCaseKey
	replyPrompt?: string
	adminPrompt?: string
	enabled?: boolean
}

export type ChatAiModeResponse = {
	roomId: string
	aiAnswerMode: AiAnswerMode
}

export type AdminTaskResponse = {
	id: string
	draftId: string
	chatRoomId: string
	text: string
	status: 'pending' | 'done' | 'dismissed'
	actedByOperatorId: string | null
	actedAt: string | null
	createdAt: string
}

export default new ChatService()
